// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { type SuiTransactionBlockResponse } from '@mysten/sui.js/client';
import clsx from 'clsx';
import { type ReactNode, useState } from 'react';

import { Signatures } from './Signatures';
import { ErrorBoundary } from '~/components/error-boundary/ErrorBoundary';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { Events } from '~/pages/transaction-result/Events';
import { TransactionData } from '~/pages/transaction-result/TransactionData';
import { TransactionSummary } from '~/pages/transaction-result/transaction-summary';
import { LOCAL_STORAGE_SPLIT_PANE_KEYS, SplitPanes } from '~/ui/SplitPanes';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/Tabs';

import styles from './TransactionResult.module.css';

function TabsContentContainer({ value, children }: { value: string; children: ReactNode }) {
    return (
        <TabsContent value={value} className="outline-none">
            <div className="mt-6 md:mt-10">{children}</div>
        </TabsContent>
    );
}

export function TransactionView({ transaction }: { transaction: SuiTransactionBlockResponse }) {
    const isMediumOrAbove = useBreakpoint('md');
    const [isCollapsed, setIsCollapsed] = useState(false);

    const hasEvents = !!transaction.events?.length;
    const transactionKindName = transaction.transaction?.data.transaction?.kind;
    const isProgrammableTransaction = transactionKindName === 'ProgrammableTransaction';

    // ==================== 💻 情况 A: PC端专用的分栏配置 ====================
    const leftPane = {
        panel: (
            <div className="overflow-y-auto rounded-2xl border border-transparent bg-[#0F172A] p-6 md:p-10 mt-4">
                <Tabs size="lg" defaultValue="summary">
                    <TabsList>
                        <TabsTrigger value="summary" className="!bg-transparent px-0 py-2 border-b-2 border-transparent data-[state=active]:border-[#38BDF8] data-[state=active]:text-[#38BDF8] transition-colors">Summary</TabsTrigger>
                        {hasEvents && <TabsTrigger value="events" className="!bg-transparent px-0 py-2 border-b-2 border-transparent data-[state=active]:border-[#38BDF8] data-[state=active]:text-[#38BDF8] transition-colors">Events</TabsTrigger>}
                        {isProgrammableTransaction && <TabsTrigger value="signatures" className="!bg-transparent px-0 py-2 border-b-2 border-transparent data-[state=active]:border-[#38BDF8] data-[state=active]:text-[#38BDF8] transition-colors">Signatures</TabsTrigger>}
                    </TabsList>

                    <TabsContentContainer value="summary">
                        <TransactionSummary transaction={transaction} />
                    </TabsContentContainer>
                    {hasEvents && (
                        <TabsContentContainer value="events">
                            <Events events={transaction.events!} />
                        </TabsContentContainer>
                    )}
                    <TabsContentContainer value="signatures">
                        <ErrorBoundary>
                            <Signatures transaction={transaction} />
                        </ErrorBoundary>
                    </TabsContentContainer>
                </Tabs>
            </div>
        ),
        minSize: 35,
        collapsible: true,
        collapsibleButton: true,
        noHoverHidden: isMediumOrAbove,
    };

    const rightPane = {
        panel: (
            <div className={clsx('h-full w-full overflow-y-auto mt-4 pr-2 ml-2', isCollapsed && 'pl-2')}>
                <TransactionData transaction={transaction} />
            </div>
        ),
        minSize: 40,
        defaultSize: isProgrammableTransaction ? 65 : 50,
    };

    return (
        <div className={clsx(styles.txdetailsbg, "w-full min-h-screen")}>

            {isMediumOrAbove ? (
                /* 💻 PC端：外层强制撑满屏幕，内部左右双卡片各自独立滚动，互不干扰 */
                <div className="h-screen max-h-screen overflow-hidden p-2">
                    <SplitPanes
                        autoSaveId={LOCAL_STORAGE_SPLIT_PANE_KEYS.TRANSACTION_VIEW}
                        onCollapse={setIsCollapsed}
                        dividerSize="md"
                        splitPanels={[leftPane, rightPane]}
                        direction="horizontal"
                    />
                </div>
            ) : (
                /* 📱 H5 移动端：维持单卡片精美聚合 Tab 模式 */
                <div className="mt-2">
                    <div className="w-full rounded-2xl bg-[#0F172A] p-3 border border-white/5 shadow-2xl">
                        <Tabs size="lg" defaultValue="summary">

                            <div className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border-b border-white/5 pb-1">
                                <TabsList className="bg-transparent border-none flex justify-start gap-6 whitespace-nowrap">
                                    <TabsTrigger
                                        value="summary"
                                        className="!bg-transparent px-0 py-2 border-b-2 border-transparent data-[state=active]:border-[#38BDF8] data-[state=active]:text-[#38BDF8] transition-colors font-medium text-sm"
                                    >
                                        Summary
                                    </TabsTrigger>

                                    {hasEvents && (
                                        <TabsTrigger
                                            value="events"
                                            className="!bg-transparent px-0 py-2 border-b-2 border-transparent data-[state=active]:border-[#38BDF8] data-[state=active]:text-[#38BDF8] transition-colors font-medium text-sm"
                                        >
                                            Events
                                        </TabsTrigger>
                                    )}

                                    {isProgrammableTransaction && (
                                        <TabsTrigger
                                            value="signatures"
                                            className="!bg-transparent px-0 py-2 border-b-2 border-transparent data-[state=active]:border-[#38BDF8] data-[state=active]:text-[#38BDF8] transition-colors font-medium text-sm"
                                        >
                                            Signatures
                                        </TabsTrigger>
                                    )}
                                    <TabsTrigger
                                        value="txdata"
                                        className="!bg-transparent px-0 py-2 border-b-2 border-transparent data-[state=active]:border-[#38BDF8] data-[state=active]:text-[#38BDF8] transition-colors font-medium text-sm"
                                    >
                                        Transaction Data
                                    </TabsTrigger>
                                </TabsList>
                            </div>

                            <TabsContentContainer value="summary">
                                <TransactionSummary transaction={transaction} />
                            </TabsContentContainer>

                            <TabsContentContainer value="txdata">
                                <div className="w-full overflow-x-auto">
                                    <TransactionData transaction={transaction} />
                                </div>
                            </TabsContentContainer>

                            {hasEvents && (
                                <TabsContentContainer value="events">
                                    <Events events={transaction.events!} />
                                </TabsContentContainer>
                            )}

                            <TabsContentContainer value="signatures">
                                <ErrorBoundary>
                                    <Signatures transaction={transaction} />
                                </ErrorBoundary>
                            </TabsContentContainer>
                        </Tabs>
                    </div>
                </div>
            )}

        </div>
    );
}